import React, { useContext, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import { ApplicationContext } from '../../context/GlobalState';
import { getLegalContent, LEGAL_LAST_UPDATED } from '../../data/legal';
import './LegalPage.css';

/**
 * Minimal, dependency-free markdown renderer.
 * Supports: ## / ### headings, paragraphs, "- " bullet lists,
 * **bold**, [text](url), and blank-line separation.
 */
function renderMarkdown(md) {
  if (!md) return null;
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let i = 0;
  let buf = [];
  const flushPara = () => {
    if (buf.length) {
      blocks.push({ type: 'p', text: buf.join(' ').trim() });
      buf = [];
    }
  };
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();
    if (!trimmed) { flushPara(); i++; continue; }
    if (trimmed.startsWith('### ')) { flushPara(); blocks.push({ type: 'h3', text: trimmed.slice(4) }); i++; continue; }
    if (trimmed.startsWith('## ')) { flushPara(); blocks.push({ type: 'h2', text: trimmed.slice(3) }); i++; continue; }
    if (trimmed.startsWith('# ')) { flushPara(); blocks.push({ type: 'h2', text: trimmed.slice(2) }); i++; continue; }
    if (trimmed.startsWith('- ')) {
      flushPara();
      const items = [];
      while (i < lines.length && lines[i].trim().startsWith('- ')) {
        items.push(lines[i].trim().slice(2));
        i++;
      }
      blocks.push({ type: 'ul', items });
      continue;
    }
    buf.push(trimmed);
    i++;
  }
  flushPara();

  const inline = (text, key) => {
    // links [t](u)
    const linkParts = [];
    let cursor = 0;
    const re = /\[([^\]]+)\]\(([^)]+)\)/g;
    let m;
    let idx = 0;
    while ((m = re.exec(text)) !== null) {
      if (m.index > cursor) linkParts.push({ t: 'text', v: text.slice(cursor, m.index) });
      linkParts.push({ t: 'link', label: m[1], href: m[2] });
      cursor = m.index + m[0].length;
    }
    if (cursor < text.length) linkParts.push({ t: 'text', v: text.slice(cursor) });

    // bold within each text part
    const renderBold = (s, k) => {
      const out = [];
      const br = /\*\*([^*]+)\*\*/g;
      let c = 0; let mm; let j = 0;
      while ((mm = br.exec(s)) !== null) {
        if (mm.index > c) out.push(<React.Fragment key={`${k}-t${j++}`}>{s.slice(c, mm.index)}</React.Fragment>);
        out.push(<strong key={`${k}-b${j++}`}>{mm[1]}</strong>);
        c = mm.index + mm[0].length;
      }
      if (c < s.length) out.push(<React.Fragment key={`${k}-t${j++}`}>{s.slice(c)}</React.Fragment>);
      return out;
    };

    return linkParts.map((p, j) => {
      if (p.t === 'text') return <React.Fragment key={`${key}-tx${j}`}>{renderBold(p.v, `${key}-tx${j}`)}</React.Fragment>;
      const isInternal = p.href.startsWith('/') && !p.href.startsWith('//');
      if (isInternal) return <Link key={`${key}-ln${j}`} to={p.href}>{p.label}</Link>;
      return <a key={`${key}-ln${j}`} href={p.href} target="_blank" rel="noopener noreferrer">{p.label}</a>;
    });
  };

  return blocks.map((b, k) => {
    if (b.type === 'h2') return <h2 key={k}>{inline(b.text, `h2-${k}`)}</h2>;
    if (b.type === 'h3') return <h3 key={k}>{inline(b.text, `h3-${k}`)}</h3>;
    if (b.type === 'ul') return (
      <ul key={k}>
        {b.items.map((it, j) => <li key={j}>{inline(it, `li-${k}-${j}`)}</li>)}
      </ul>
    );
    return <p key={k}>{inline(b.text, `p-${k}`)}</p>;
  });
}

const TITLES = {
  privacy: 'Privacy Policy',
  terms: 'SMS Messaging Terms & Conditions',
  ada: 'Accessibility Statement',
  sitemap: 'Sitemap',
};

export default function LegalPage({ slug }) {
  const { state } = useContext(ApplicationContext);
  const location = useLocation();
  const brand = state.brand;
  const brandKey = brand?.brandKey || brand?.key || 'gso';
  const brandName = brand?.name || 'Our Practice';
  const baseDomain = brand?.domain || 'gaspineortho.com';
  const content = useMemo(() => getLegalContent(brandKey, slug), [brandKey, slug]);

  const heading = TITLES[slug] || 'Legal';
  const pageTitle = `${heading} | ${brandName}`;
  const canonicalUrl = `https://${baseDomain}${location.pathname}`;

  const renderBody = () => {
    if (slug === 'sitemap') {
      const sm = content || { sections: [] };
      return (
        <div className="legal-sitemap">
          {(sm.sections || []).map((sec, i) => (
            <section key={i} className="legal-sitemap-section">
              <h2>{sec.title}</h2>
              <ul>
                {(sec.links || []).map((ln, j) => (
                  <li key={j}>
                    {ln.href.startsWith('/')
                      ? <Link to={ln.href}>{ln.label}</Link>
                      : <a href={ln.href} target="_blank" rel="noopener noreferrer">{ln.label}</a>}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      );
    }
    if (!content) return <p>Content unavailable.</p>;
    return renderMarkdown(content);
  };

  return (
    <main className="legal-page">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={`${heading} for ${brandName}`} />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      <section className="legal-hero glass-dark">
        <div className="container">
          <h1>{heading}</h1>
          <p className="legal-meta">{brandName} · Last updated: {LEGAL_LAST_UPDATED}</p>
        </div>
      </section>

      <section className="container legal-body-wrap">
        <article className="glass-panel legal-body">
          {renderBody()}
        </article>
      </section>
    </main>
  );
}
